import map from 'lodash/map'
import filter from 'lodash/filter'
import * as React from 'react'
import {Button} from '@frontrow/console/src/components/button'
import {H2} from '@frontrow/console/src/components/typography'
import {
  type SyncResultT,
  type SyncResultFailureT,
  type SyncResultSuccessT
} from '@frontrow/console/src/entities/models/roster-sync'
import {partitionResults} from '@frontrow/console/src/entities/logic/roster-sync'

import RosterSyncSuccess from '@frontrow/console/src/components/pages/roster-page/roster-sync-page/sync-success'
import RosterSyncSuccessFailure from '@frontrow/console/src/components/pages/roster-page/roster-sync-page/sync-success-failure'
import RosterSyncFailure from '@frontrow/console/src/components/pages/roster-page/roster-sync-page/sync-error'
import RosterSyncSubmitModal from '@frontrow/console/src/components/pages/roster-page/roster-sync-page/submit-modal'

type Props = {
  results: Array<SyncResultT>
  handleSubmit?: () => Promise<void>
}

export default function RosterSyncResults(props: Props): React.ReactElement {
  const [showSubmitModal, setShowSubmitModal] = React.useState<boolean>(false)

  const toggleSubmitModal = () => {
    setShowSubmitModal(!showSubmitModal)
  }

  const {results, handleSubmit} = props
  const [failures, successes] = partitionResults(results)
  const mayFailureBlock = failureBlock(failures, successes)
  const maySubmit = handleSubmit ? (
    <React.Fragment>
      <RosterSyncSubmitModal
        show={showSubmitModal}
        handleClose={toggleSubmitModal}
        handleSubmit={handleSubmit}
      />
      <Button onClick={toggleSubmitModal} type="submit" style="primary">
        Confirm
      </Button>
    </React.Fragment>
  ) : null
  if (mayFailureBlock === null) {
    return (
      <React.Fragment>
        <RosterSyncSuccess successes={successes} />
        {maySubmit}
      </React.Fragment>
    )
  } else {
    return mayFailureBlock
  }
}

function failureBlock(
  failures: Array<SyncResultFailureT>,
  successes: Array<SyncResultSuccessT>
): React.ReactElement {
  const hardFailures = map(failures, (failure, i: number) => (
    <RosterSyncFailure key={i} failure={failure} />
  ))
  const softFailures = map(filter(successes, hasFailures), (stats, i: number) => (
    <RosterSyncSuccessFailure key={i} stats={stats} />
  ))
  if (hardFailures.length > 0 || softFailures.length > 0) {
    return (
      <React.Fragment>
        <H2>Please fix the errors below and re-upload the CSVs.</H2>
        {hardFailures}
        {softFailures}
      </React.Fragment>
    )
  }
  return <></>
}

function hasFailures(stats: SyncResultSuccessT): boolean {
  const {failedItems} = stats
  const numFailures =
    failedItems.failedStudents.length +
    failedItems.failedTeachers.length +
    failedItems.failedCourses.length +
    failedItems.failedCourseMemberships.length
  return numFailures > 0
}
